import { useEffect, useRef } from 'react';
import { isNative } from '@/lib/capacitor';
import {
  pushOverlayEntry,
  releaseOverlayEntry,
} from '@/lib/history-interceptor';

let nextId = 0;

/**
 * Pushes a history entry when an overlay opens and pops it on close.
 * On Android, the hardware Back button calls history.back() which pops
 * the entry → popstate fires → overlay closes instead of navigating away.
 * The iOS edge-swipe-back gesture works the same way.
 *
 * Only active on native platforms (Capacitor). On web, overlays close
 * normally via click/escape without history manipulation.
 *
 * Handles nested overlays via LIFO ordering of history entries. The popstate
 * handling lives in lib/history-interceptor.js, which runs before Inertia's
 * listener: Inertia would otherwise restore the page under the overlay and
 * remount it (#524).
 */
export default function useBackDismiss({ open, onClose }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (!isNative()) return;
    if (!open) return;

    const overlay = pushOverlayEntry({
      marker: `overlay-${nextId++}`,
      close: () => onCloseRef.current(),
      isOpen: () => openRef.current,
    });

    // Programmatic close (selection, swipe-to-dismiss, overlay tap) or
    // unmount: clean up our hash entry without a popstate. history.back()
    // fires one, which made Inertia restore the page with preserveState:false
    // and wipe the form (why native combobox selections "didn't stick").
    return () => releaseOverlayEntry(overlay);
  }, [open]);
}
